'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import { useCallback, useEffect, useRef } from 'react';
import type { VideoRecommendationItem } from './types';
import { imageAlt } from '@/lib/render/imageAlt';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { parseVideoUrl, postPlayerCommand, toEmbedUrl } from '@/lib/videoEmbed';

interface VideoCardProps {
  video: VideoRecommendationItem;
  isOpen: boolean;
  isPlaying: boolean;
  onPlay: () => void;
  /**
   * Lifts the card's 277px thumbnail cap to 85 % of the available width, for
   * the desktop row that holds a single video (SH-445). Without it a lone
   * video renders at 277px in the middle of a full-width row, which is what
   * made it look small rather than "full width".
   */
  enlarged?: boolean;
  /**
   * Stands in for the title wherever the video has to be *named* — the
   * thumbnail `alt`, the play button and the iframe. The title is optional
   * (SH-445), and the thumbnail is the only cue identifying a video, so an
   * empty `alt` would leave it unannounceable. The parent supplies it because
   * only it knows the video's position in the list.
   */
  fallbackLabel: string;
}

/**
 * Keeps an open player in sync with `isPlaying`, in the provider's own
 * postMessage dialect. A command sent before the player document has loaded
 * is lost, so a card closed by a click on another video before its own
 * player was ready would still autoplay. The returned `onLoad` replays the
 * latest state once the iframe has loaded.
 */
function usePlayerSync(videoUrl: string, isOpen: boolean, isPlaying: boolean) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const provider = parseVideoUrl(videoUrl)?.provider;
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  const sync = useCallback(() => {
    if (!provider) {
      return;
    }
    postPlayerCommand(
      iframeRef.current,
      provider,
      isPlayingRef.current ? 'play' : 'pause',
    );
  }, [provider]);

  useEffect(() => {
    if (isOpen) {
      sync();
    }
  }, [isPlaying, isOpen, sync]);

  return { iframeRef, onLoad: sync };
}

const PlayIcon = () => (
  <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white pl-1 transition-colors group-hover:bg-white/20">
    <svg
      width="14"
      height="14"
      viewBox="0 0 12 14"
      fill="none"
      aria-hidden="true"
    >
      <path d="M0 0l12 7L0 14V0z" fill="white" />
    </svg>
  </div>
);

// Capped at 277px on desktop (max-w-[277px]); full padded width on mobile.
const THUMBNAIL_SIZES = '(max-width: 767px) calc(100vw - 2rem), 277px';
// A lone video (SH-445) is max-w-[85%] of a row of at most 1056px (max-w-6xl
// minus lg:px-9 and the card's px-3), so ~898px.
const ENLARGED_THUMBNAIL_SIZES =
  '(max-width: 767px) calc(100vw - 2rem), min(85vw, 898px)';

function Thumbnail({
  src,
  alt,
  enlarged = false,
}: {
  src: string | null;
  alt: string;
  enlarged?: boolean;
}) {
  return src ? (
    <Image
      src={src}
      alt={alt}
      fill
      sizes={enlarged ? ENLARGED_THUMBNAIL_SIZES : THUMBNAIL_SIZES}
      className="object-contain"
    />
  ) : (
    <div className="h-full w-full bg-gray-200" />
  );
}

export const VideoCard = ({
  video,
  isOpen,
  isPlaying,
  onPlay,
  fallbackLabel,
  enlarged = false,
}: VideoCardProps) => {
  const { imageUrl, substitutePlaceholders: sub } = useRenderContext();
  // The editor types these; a `{domain.*}` token must not reach the page —
  // nor a screen reader through the alt/aria strings below.
  const title = video.title ? sub(video.title) : '';
  const description = video.description ? sub(video.description) : '';
  // `title` drives what is *shown*, `label` what the video is *called*.
  const label = title || fallbackLabel;
  const { iframeRef, onLoad } = usePlayerSync(
    video.videoUrl,
    isOpen,
    isPlaying,
  );

  const thumbnailSrc = video.thumbnail ? imageUrl(video.thumbnail) : null;
  // The thumbnail is a picture of its own, so the media's alt text names it;
  // the video's label stands in while no alt text is set.
  const thumbnailAlt = sub(imageAlt(video.thumbnail)) || label;
  const embedSrc = toEmbedUrl(video.videoUrl, {
    autoplay: true,
    controllable: true,
  });

  return (
    <div className="px-3 pb-4 text-center">
      <div
        className={classNames(
          'relative mx-auto mb-2 aspect-square w-full overflow-hidden rounded-2xl',
          enlarged ? 'max-w-[85%]' : 'max-w-[277px]',
        )}
      >
        {isOpen ? (
          <iframe
            ref={iframeRef}
            onLoad={onLoad}
            src={embedSrc}
            title={label}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <button
            type="button"
            className="group relative h-full w-full"
            onClick={onPlay}
            aria-label={`${label} abspielen`}
          >
            <Thumbnail
              src={thumbnailSrc}
              alt={thumbnailAlt}
              enlarged={enlarged}
            />
            <div className="absolute inset-0 flex items-center justify-center group-hover:bg-black/30">
              <PlayIcon />
            </div>
          </button>
        )}
      </div>
      {title && <div className="font-medium">{title}</div>}
      {description && <div>{description}</div>}
    </div>
  );
};

/** Mobile-only card: single slide, no text below (captions handled by Carousel). */
export const VideoCardMobile = ({
  video,
  isOpen,
  isPlaying,
  onPlay,
  fallbackLabel,
}: VideoCardProps) => {
  const { imageUrl, substitutePlaceholders: sub } = useRenderContext();
  // The editor types these; a `{domain.*}` token must not reach the page —
  // nor a screen reader through the alt/aria strings below.
  const title = video.title ? sub(video.title) : '';
  const label = title || fallbackLabel;
  const { iframeRef, onLoad } = usePlayerSync(
    video.videoUrl,
    isOpen,
    isPlaying,
  );

  const thumbnailSrc = video.thumbnail ? imageUrl(video.thumbnail) : null;
  // The thumbnail is a picture of its own, so the media's alt text names it;
  // the video's label stands in while no alt text is set.
  const thumbnailAlt = sub(imageAlt(video.thumbnail)) || label;
  const embedSrc = toEmbedUrl(video.videoUrl, {
    autoplay: true,
    controllable: true,
  });

  if (isOpen) {
    return (
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl">
        <iframe
          ref={iframeRef}
          onLoad={onLoad}
          src={embedSrc}
          title={label}
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className="group relative aspect-square w-full overflow-hidden rounded-2xl"
      onClick={onPlay}
      aria-label={`${label} abspielen`}
    >
      <Thumbnail src={thumbnailSrc} alt={thumbnailAlt} />
      <div className="absolute inset-0 flex items-center justify-center group-hover:bg-black/30">
        <PlayIcon />
      </div>
    </button>
  );
};
